import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getUnixTime } from 'date-fns';
import { LoginLink, LoginLinkAPI } from 'lib/api/firebase/apis/api-login-link';
import { queryKeyFactory } from 'lib/utils/queryKeyFactory';
import { sortBy } from 'lodash';
import { useMemo } from 'react';

export function useLoginLinkAPI() {
  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: LoginLinkAPI.create,
    onSuccess(_data, variables) {
      queryClient.invalidateQueries({
        queryKey: queryKeyFactory.patient.loginLink.root(variables!.uid)
      });
    }
  });

  const generateToken = useMutation({
    mutationFn: LoginLinkAPI.getTokenForLink
  });

  const deactivate = useMutation({
    mutationFn: (args: {
      assessmentLinkId: string;
      /**
       * NOTE: This field is only needed in order to invalidate the query cache for the patient
       */
      patientId: string;
    }) => LoginLinkAPI.deactivate({ id: args.assessmentLinkId }),
    onSuccess(_data, variables) {
      queryClient.invalidateQueries({
        queryKey: queryKeyFactory.patient.loginLink.root(variables.patientId)
      });
    }
  });

  const consumeToken = useMutation({
    mutationFn: LoginLinkAPI.consumeToken
  });
  return {
    create,
    deactivate,
    generateToken,
    consumeToken
  };
}

export function useGetLoginLinkToken(id: LoginLink['id']) {
  return useQuery({
    queryKey: queryKeyFactory.patient.loginLink.token(id),
    queryFn: () => LoginLinkAPI.getTokenForLink({ id })
  });
}

export function useLoginLinkListQuery(uid: string) {
  return useQuery({
    queryKey: queryKeyFactory.patient.loginLink.list(uid),
    queryFn: () => LoginLinkAPI.list({ uid: uid })
  });
}

export function useGetLatestLoginLink(uid: string) {
  const listQuery = useLoginLinkListQuery(uid);
  const latest = useMemo(
    () =>
      sortBy(
        listQuery.data?.data
        // Active
        .filter(_ => _.isActive)
        // Not expired
        .filter(_ => getUnixTime(new Date()) < _.expiresAt)
        ,
        _ => -_.expiresAt
      ).at(0),
    [listQuery.data]
  );

  return { latest, listQuery };
}
